import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Stethoscope, Search, X, Check } from 'lucide-react'
import { providersAPI } from '../services/api'
import { PCP_FINDER_TAXONOMY } from '../utils/specialtyChoices' // PROD-1183: broad on purpose (see there)
import { pcpFromDirectoryRow, pcpFromCardOnly, pcpSearchText, rowMatchesCardPcp, pcpNeededFor, pcpQuery } from '../utils/pcpChoice'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * PrimaryCareDoctor — s101 (D497). Profile › Insurance: the patient's primary care doctor.
 *   ★ From the card: the scan reads the PCP printed on (HMO) cards; we look it up in the national
 *     directory and ask the patient to confirm the match - never saved without that tap.
 *   ★ Or the patient picks one (name search, primary-care doctors near their ZIP).
 *   Stored as insurance.pcp. It is what referral requests and "send to my PCP" will use.
 */
const NAVY = '#15354F'
const BLUE = '#46A8F9'

export default function PrimaryCareDoctor({ value, cardPcp, zip, coverageType, onChange, t }) {
  const tr = (k, d, o) => (t ? t(k, { defaultValue: d, ...(o || {}) }) : d)
  const [match, setMatch] = useState({ state: 'idle', row: null })
  const [sheet, setSheet] = useState(false)
  const [q, setQ] = useState('')
  // ★ s110 (TestFlight b77, Dr. Rami: "Need a proper way to search for Pcp. The results show up all over the place
  //   ... Should have name / practice, Npi, Location city/state/zip"; mockup "Orella Doctors Start Case" P2).
  const [place, setPlace] = useState('')
  const [npi, setNpi] = useState('')
  const [chosen, setChosen] = useState(null)
  const [rows, setRows] = useState({ loading: false, list: [], failed: false })

  // The card's PCP -> one directory lookup (by name, near the patient), offered for confirmation.
  useEffect(() => {
    if (value || !cardPcp || !cardPcp.name) { setMatch({ state: 'idle', row: null }); return undefined }
    let alive = true
    setMatch({ state: 'loading', row: null })
    providersAPI.search({ q: pcpSearchText(cardPcp.name), zip: zip || undefined, taxonomy: PCP_FINDER_TAXONOMY.join(','), radiusMiles: 50, limit: 10 })
      .then((r) => {
        if (!alive) return
        const hit = (Array.isArray(r?.results) ? r.results : []).find((x) => rowMatchesCardPcp(x, cardPcp.name)) || null
        setMatch({ state: hit ? 'found' : 'none', row: hit })
      })
      .catch(() => { if (alive) setMatch({ state: 'none', row: null }) })
    return () => { alive = false }
  }, [value, cardPcp && cardPcp.name, zip]) // eslint-disable-line react-hooks/exhaustive-deps

  // The picker opens on the patient's own ZIP as the place.
  useEffect(() => { if (sheet) { setPlace((p) => p || zip || ''); setChosen(null) } }, [sheet]) // eslint-disable-line react-hooks/exhaustive-deps

  // The picker's search: an NPI alone, or a name / practice within the place (utils/pcpChoice pcpQuery).
  const query = pcpQuery({ q, npi, place })
  useEffect(() => {
    if (!sheet) return undefined
    if (!query.ready) { setRows({ loading: false, list: [], failed: false }); return undefined }
    let alive = true
    setRows((s) => ({ ...s, loading: true }))
    const h = setTimeout(() => {
      providersAPI.pcpSearch({ q: query.q, npi: query.npi, place: query.place, zip, taxonomy: PCP_FINDER_TAXONOMY.join(',') })
        .catch((e) => {
          // Until API migration v243_360_0 is applied: the old name search near the patient.
          if (e && e.code === 'pcp_search_unavailable' && query.q) {
            return providersAPI.search({ q: query.q, zip: zip || undefined, taxonomy: PCP_FINDER_TAXONOMY.join(','), radiusMiles: 50, limit: 25 })
          }
          throw e
        })
        .then((r) => { if (alive) { setRows({ loading: false, list: Array.isArray(r?.results) ? r.results : [], failed: false }); setChosen(null) } })
        .catch(() => { if (alive) setRows({ loading: false, list: [], failed: true }) })
    }, 400)
    return () => { alive = false; clearTimeout(h) }
  }, [query.key, sheet, zip]) // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (row, source) => { onChange(pcpFromDirectoryRow(row, source)); setSheet(false); setQ(''); setNpi(''); setChosen(null) }
  const needed = pcpNeededFor(coverageType)

  return (
    <div className="mt-2 mb-4" data-testid="pcp-section">
      <div className="border-t border-gray-100 my-5" />
      <p className="text-[11px] font-bold uppercase tracking-widest mb-1 pb-1 inline-block" style={{ color: NAVY, borderBottom: `1.5px solid ${NAVY}` }}>
        {tr('pcp.title', 'Primary care doctor')}
      </p>
      {/* s110 (b77): no "Optional" line — only an HMO-style plan needs one, and we encourage it for everyone. */}
      {needed ? (
        <p className="text-[12px] text-gray-500 mb-3">{tr('pcp.neededHmo', 'Your plan usually needs your primary care doctor for referrals to specialists.')}</p>
      ) : <div className="mb-3" />}

      {value ? (
        <div className="rounded-2xl border border-[#E5EDF3] bg-white p-3.5 flex gap-3 items-start" data-testid="pcp-chosen">
          <span className="w-10 h-10 rounded-xl grid place-items-center flex-none" style={{ background: '#E9F8FF' }}><Stethoscope size={18} color={BLUE} /></span>
          <span className="flex-1 min-w-0">
            <b className="block text-[14.5px]" style={{ color: NAVY }}>{value.name}{value.credential ? `, ${value.credential}` : ''}</b>
            {value.specialty && <span className="block text-[12.5px] text-gray-500">{value.specialty}</span>}
            {value.address && <span className="block text-[12.5px] text-gray-500">{value.address}</span>}
            {value.phone && <span className="block text-[12.5px] text-gray-500">{value.phone}</span>}
            <span className="flex gap-4 mt-1.5">
              <button type="button" onClick={() => setSheet(true)} className="text-[13px] font-bold" style={{ color: BLUE }}>{tr('pcp.change', 'Change')}</button>
              <button type="button" onClick={() => onChange(null)} className="text-[13px] font-bold text-gray-500">{tr('pcp.remove', 'Remove')}</button>
            </span>
          </span>
        </div>
      ) : cardPcp && cardPcp.name ? (
        <div className="rounded-2xl border border-[#E5EDF3] bg-white p-3.5 flex flex-col gap-2" data-testid="pcp-from-card">
          <span className="text-[12px] text-gray-500">{tr('pcp.fromCard', 'From your insurance card')}</span>
          <b className="text-[14.5px]" style={{ color: NAVY }}>{cardPcp.name}</b>
          {cardPcp.phone && <span className="text-[12.5px] text-gray-500">{cardPcp.phone}</span>}
          {match.state === 'loading' && <span className="text-[12.5px] text-gray-500 flex items-center gap-1.5"><OrellaSpinner size={16} tone="inherit" /> {tr('pcp.looking', 'Looking them up…')}</span>}
          {match.state === 'found' && match.row && (
            <div className="rounded-xl p-2.5 text-[12.5px]" style={{ background: '#EEF9F1', color: '#166534' }}>
              <b className="block">{tr('pcp.matched', 'Found in the doctor directory')}</b>
              {match.row.name}{match.row.address?.city ? ` · ${match.row.address.city}` : ''}{match.row.onOrella ? ` · ${tr('pcp.onOrella', 'on Orella')}` : ''}
            </div>
          )}
          <div className="flex flex-col gap-2 mt-1">
            {match.state === 'found' && match.row && (
              <button type="button" onClick={() => pick(match.row, 'card')} data-cta="pcp-confirm-card" className="w-full py-3 rounded-full font-bold text-white text-[14px] flex items-center justify-center gap-1.5" style={{ background: NAVY }}>
                <Check size={15} /> {tr('pcp.thatsMine', "That's my doctor")}
              </button>
            )}
            {match.state === 'none' && (
              <button type="button" onClick={() => onChange(pcpFromCardOnly(cardPcp))} data-cta="pcp-save-card" className="w-full py-3 rounded-full font-bold text-white text-[14px]" style={{ background: NAVY }}>
                {tr('pcp.saveAsOnCard', 'Save as shown on my card')}
              </button>
            )}
            <button type="button" onClick={() => setSheet(true)} className="w-full py-3 rounded-full font-bold text-[14px] border-[1.5px] border-[#D8E6F0]" style={{ color: NAVY }}>
              {tr('pcp.chooseDifferent', 'Choose a different doctor')}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setSheet(true)} data-cta="pcp-add" className="w-full rounded-2xl border border-dashed border-[#A7D9FC] p-3.5 flex items-center gap-3 text-left">
          <span className="w-10 h-10 rounded-xl grid place-items-center flex-none" style={{ background: '#E9F8FF' }}><Stethoscope size={18} color={BLUE} /></span>
          <span className="flex-1 text-[14px] font-bold" style={{ color: NAVY }}>{tr('pcp.add', 'Add your primary care doctor')}</span>
        </button>
      )}
      <p className="text-[12.5px] leading-snug mt-2.5" style={{ color: '#4E6577' }} data-testid="pcp-preselect-note">
        {tr('pcp.preselectNote', 'Your primary care doctor is preselected when you choose where to send a case. You can always pick someone else.')}
      </p>

      {sheet && createPortal(
        <div data-no-top-inset="bottom-anchored sheet (justify-end, max-h 88%, pays the bottom inset)" className="fixed inset-0 z-[60] flex flex-col justify-end" style={{ background: 'rgba(15,31,46,0.38)' }} onClick={(e) => { if (e.target === e.currentTarget) setSheet(false) }}>
          <div className="bg-white rounded-t-[24px] px-5 pt-3 max-h-[88%] flex flex-col" style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }} data-testid="pcp-sheet">
            <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: '#D8E6F0' }} />
            <div className="flex items-center justify-between mb-3">
              <span className="text-[17px] font-extrabold" style={{ color: NAVY }}>{tr('pcp.findTitle', 'Find your primary care doctor')}</span>
              <button type="button" onClick={() => setSheet(false)} className="p-1" aria-label={tr('common.close', 'Close')}><X size={20} className="text-gray-400" /></button>
            </div>
            <label className="block text-[12.5px] font-bold mb-1" style={{ color: '#4E6577' }} htmlFor="pcp-search">{tr('pcp.nameLabel', 'Doctor or practice name')}</label>
            <div className="relative mb-2.5">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input id="pcp-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr('pcp.searchHint', 'Doctor or practice name')}
                className="w-full rounded-xl pl-9 pr-3 py-2.5 text-[14.5px] outline-none" style={{ border: `1.5px solid ${q ? BLUE : '#E5EDF3'}`, color: NAVY }} />
            </div>
            <div className="grid grid-cols-2 gap-2.5 mb-3">
              <span className="min-w-0">
                <label className="block text-[12.5px] font-bold mb-1" style={{ color: '#4E6577' }} htmlFor="pcp-place">{tr('pcp.placeLabel', 'City, state or ZIP')}</label>
                <input id="pcp-place" value={place} onChange={(e) => setPlace(e.target.value)} data-testid="pcp-place"
                  className="w-full min-w-0 rounded-xl px-3 py-2.5 text-[14.5px] outline-none" style={{ border: '1.5px solid #E5EDF3', color: NAVY }} />
              </span>
              <span className="min-w-0">
                <label className="block text-[12.5px] font-bold mb-1" style={{ color: '#4E6577' }} htmlFor="pcp-npi">{tr('pcp.npiLabel', 'NPI (if you know it)')}</label>
                <input id="pcp-npi" value={npi} onChange={(e) => setNpi(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))} inputMode="numeric" data-testid="pcp-npi"
                  placeholder={tr('pcp.npiHint', '10 digits')}
                  className="w-full min-w-0 rounded-xl px-3 py-2.5 text-[14.5px] outline-none" style={{ border: '1.5px solid #E5EDF3', color: NAVY }} />
              </span>
            </div>
            {query.npiInvalid && <span className="text-[12.5px] mb-2" style={{ color: '#B45309' }}>{tr('pcp.npiInvalid', 'An NPI has 10 digits.')}</span>}
            <div className="overflow-auto flex-1 flex flex-col gap-2 pb-2">
              {rows.loading && <span className="text-[13px] text-gray-500 flex items-center gap-1.5"><OrellaSpinner size={16} tone="inherit" /> {tr('pcp.searching', 'Searching…')}</span>}
              {rows.failed && <span className="text-[13px]" style={{ color: '#B45309' }}>{tr('pcp.searchFailed', "We couldn't search right now. Please try again.")}</span>}
              {!rows.loading && !rows.failed && query.ready && rows.list.length === 0 && <span className="text-[13px] text-gray-500">{tr('pcp.noResults', 'No primary care doctors match. Try the practice name, a nearby city or the NPI.')}</span>}
              {!rows.loading && !rows.failed && rows.list.length > 0 && query.place && !query.npi && (
                <span className="text-[12px] font-bold" style={{ color: '#5F7A8E' }}>{rows.list.length === 1 ? tr('pcp.resultsInOne', '1 primary care doctor in {{place}}', { place: query.place }) : tr('pcp.resultsIn', '{{count}} primary care doctors in {{place}}', { count: rows.list.length, place: query.place })}</span>
              )}
              {rows.list.map((r) => {
                const sel = chosen && chosen.npi === r.npi
                const street = [r.address?.line1, r.address?.city, [r.address?.state, (r.address?.zip || '').slice(0, 5)].filter(Boolean).join(' ')].filter(Boolean).join(', ')
                return (
                  <button key={r.npi} type="button" onClick={() => setChosen(r)} data-testid="pcp-result" className="rounded-2xl p-3 text-left flex flex-col gap-0.5"
                          style={sel ? { border: `1.5px solid ${BLUE}`, background: '#F4FBFF' } : { border: '1px solid #E5EDF3', background: '#fff' }}>
                    <span className="flex justify-between gap-2">
                      <b className="text-[14.5px]" style={{ color: NAVY }}>{r.name}{r.credential ? `, ${r.credential}` : ''}</b>
                      {typeof r.miles === 'number' && <span className="text-[12px] font-bold flex-none" style={{ color: '#5F7A8E' }}>{r.addressConfirmed === false ? '~' : ''}{Math.round(r.miles * 10) / 10} mi</span>}
                    </span>
                    <span className="text-[12.5px] font-bold" style={{ color: BLUE }}>{[r.specialty, r.practice].filter(Boolean).join(' \u00b7 ')}</span>
                    {street && <span className="text-[12.5px]" style={{ color: '#4E6577' }}>{street}{r.addressConfirmed === false ? ` \u00b7 ${tr('findDoctor.addressUnconfirmed', 'Address not confirmed')}` : ''}</span>}
                    <span className="text-[12px]" style={{ color: '#5F7A8E' }}>{tr('pcp.npiLine', 'NPI {{npi}}', { npi: r.npi })}{r.onOrella ? ` \u00b7 ${tr('pcp.onOrella', 'on Orella')}` : ''}</span>
                  </button>
                )
              })}
            </div>
            {chosen && (
              <button type="button" onClick={() => pick(chosen, 'picked')} data-cta="pcp-confirm-pick" className="w-full py-3.5 rounded-[14px] font-extrabold text-white text-[15px] mt-1 flex items-center justify-center gap-1.5" style={{ background: NAVY }}>
                <Check size={15} /> {tr('pcp.thatsMine', "That's my doctor")}
              </button>
            )}
          </div>
        </div>, document.body)}
    </div>
  )
}
